import type { ExplorerFinding } from '../../../../models/explorer/ExplorerFinding';
import type { FindingRule, FindingRuleContext } from '../types';
import { isOneOfService, iterEdgesOfKind } from './_graphHelpers';

/**
 * P0.3 — orphan security group.
 * Severity: info (cleanup opportunity). SOC 2 CC7.1 (hygiene).
 *
 * A SG referenced by no ENI / instance / resource via the current
 * graph's flow edges.
 */
export const orphanSecurityGroupRule: FindingRule = {
  id: 'orphan_security_group',
  label: 'Orphan security group',
  description: 'Security group referenced by no resource.',

  run(ctx: FindingRuleContext): readonly ExplorerFinding[] {
    const referenced = new Set<string>();
    for (const edge of iterEdgesOfKind(ctx.graph.edges, ['uses-sg', 'connects', 'applies-to'])) {
      referenced.add(edge.to);
      referenced.add(edge.from);
    }
    const findings: ExplorerFinding[] = [];
    for (const node of ctx.graph.nodes) {
      if (!isOneOfService(node, ['security-group', 'sg'])) continue;
      if (referenced.has(node.id)) continue;
      findings.push({
        id: `orphan_security_group:resource:${node.id}`,
        ruleId: 'orphan_security_group',
        severity: 'info',
        scope: 'resource',
        subject: { kind: 'resource', id: node.id },
        title: 'Orphan security group',
        detail: `${String(node.label ?? node.id)} is not attached to any resource — safe to delete pending owner confirmation.`,
      });
    }
    return findings;
  },
};
